import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import "./App.scss";
import { useSelector } from "react-redux";
import Main from "./pages/Main/Main";
import Home from "./pages/Home/Home";
import Admins from "./pages/Admins/Admins";
import Users from "./pages/Users/Users";
import ProductOrders from "./pages/ProductOrders/ProductOrders";
import EditPartners from "./pages/EditPartners/EditPartners";
import AddPartners from "./pages/AddPartners/AddPartners";
import Shops from "./pages/Shops/Shops";
import Departments from "./pages/Departments/Departments";
import Products from "./pages/Products/Products";
import AddProducts from "./pages/AddProducts/AddProducts";
import Banners from "./pages/Banners/Banners";
import Settings from "./pages/Settings/Settings";
import Login from "./pages/Login/Login";
import Permissions from "./pages/Permissions/Permissions";
import User from "./pages/User/User";
import Partner from "./pages/Partner/Partner";
import Factories from "./pages/Factories/Factories";
import DiscountCodes from "./pages/DiscountCodes/DiscountCodes";
import Notifications from "./pages/Notifications/Notifications";
import CreateNotification from "./pages/Notifications/CreateNotification/CreateNotification";
import Support from "./pages/Support/Support";
import CreateProduct from "./pages/Products/CreateProduct/CreateProduct";
import UpdateProduct from "./pages/Products/UpdateProduct/UpdateProduct";
import ViewProduct from "./pages/Products/ViewProduct/ViewProduct";
import ViewProductOrder from "./pages/ProductOrders/ViewProductOrder/ViewProductOrder";
import Profile from "./pages/Profile/Profile";
import usePermissions from "./hooks/usePermissions"; // Adjust the import path as needed
import ContactUs from "./pages/ContactUs/ContactUs";

// Public Route Wrapper Component (for login page)
const PublicRoute = ({ children }) => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Permission Route Wrapper Component
const PermissionRoute = ({ children, requiredPermissionId }) => {
  const { hasPermission } = usePermissions();
  const { isAuthenticated } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission(requiredPermissionId)) {
    return <Navigate to="/" replace />; // Or to a "No Permission" page
  }

  return children;
};

// Router Configuration Component
const RouterConfig = () => {
  const { isAuthenticated } = useSelector((state) => state.auth);

  const router = createBrowserRouter([
    {
      path: "/login",
      element: (
        <PublicRoute>
          <Login />
        </PublicRoute>
      ),
    },
    {
      path: "/",
      element: isAuthenticated ? <Main /> : <Navigate to="/login" replace />,
      children: [
        {
          path: "/",
          element: (
            <PermissionRoute requiredPermissionId={1}>
              <Home />
            </PermissionRoute>
          ),
        },
        {
          path: "/profile",
          element: <Profile />,
        },
        {
          path: "/admins",
          element: (
            <PermissionRoute requiredPermissionId={14}>
              <Admins />
            </PermissionRoute>
          ),
        },
        {
          path: "/admins/permissions",
          element: (
            <PermissionRoute requiredPermissionId={19}>
              <Permissions />
            </PermissionRoute>
          ),
        },
        {
          path: "/admins/permissions/:id",
          element: (
            <PermissionRoute requiredPermissionId={19}>
              <Permissions />
            </PermissionRoute>
          ),
        },
        {
          path: "/users",
          element: (
            <PermissionRoute requiredPermissionId={22}>
              <Users />
            </PermissionRoute>
          ),
        },
        {
          path: "/users/:id",
          element: (
            <PermissionRoute requiredPermissionId={27}>
              <User />
            </PermissionRoute>
          ),
        },
        {
          path: "/add-products",
          element: (
            <PermissionRoute requiredPermissionId={199}>
              <AddProducts />
            </PermissionRoute>
          ),
        },
        // {
        //   path: "/edit-products/:id",
        //   element: (
        //     <PermissionRoute requiredPermissionId={81}>
        //       <UpdateProduct />
        //     </PermissionRoute>
        //   ),
        // },
        {
          path: "/edit-partners",
          element: (
            <PermissionRoute requiredPermissionId={39}>
              <EditPartners />
            </PermissionRoute>
          ),
        },
        {
          path: "/add-partners",
          element: (
            <PermissionRoute requiredPermissionId={95}>
              <AddPartners />
            </PermissionRoute>
          ),
        },
        {
          path: "/shops",
          element: (
            <PermissionRoute requiredPermissionId={29}>
              <Shops />
            </PermissionRoute>
          ),
        },
        {
          path: "/factories",
          element: (
            <PermissionRoute requiredPermissionId={129}>
              <Factories />
            </PermissionRoute>
          ),
        },
        {
          path: "/partners/:id",
          element: (
            <PermissionRoute requiredPermissionId={34 || 134}>
              <Partner />
            </PermissionRoute>
          ),
        },
        {
          path: "/departments",
          element: (
            <PermissionRoute requiredPermissionId={92}>
              <Departments />
            </PermissionRoute>
          ),
        },
        {
          path: "/discount-codes",
          element: (
            <PermissionRoute requiredPermissionId={49}>
              <DiscountCodes />
            </PermissionRoute>
          ),
        },
        {
          path: "/notifications",
          element: (
            <PermissionRoute requiredPermissionId={114}>
              <Notifications />
            </PermissionRoute>
          ),
        },
        {
          path: "/notifications/create",
          element: (
            <PermissionRoute requiredPermissionId={114}>
              <CreateNotification />
            </PermissionRoute>
          ),
        },
        {
          path: "/products",
          element: (
            <PermissionRoute requiredPermissionId={78}>
              <Products />
            </PermissionRoute>
          ),
        },
        {
          path: "/products/:id",
          element: (
            <PermissionRoute requiredPermissionId={83}>
              <ViewProduct />
            </PermissionRoute>
          ),
        },
        {
          path: "/products/create",
          element: (
            <PermissionRoute requiredPermissionId={80}>
              <CreateProduct />
            </PermissionRoute>
          ),
        },
        {
          path: "/products/edit/:id",
          element: (
            <PermissionRoute requiredPermissionId={81}>
              <UpdateProduct />
            </PermissionRoute>
          ),
        },
        {
          path: "/product-orders",
          element: (
            <PermissionRoute requiredPermissionId={68}>
              <ProductOrders />
            </PermissionRoute>
          ),
        },
        {
          path: "/product-orders/:id",
          element: (
            <PermissionRoute requiredPermissionId={141}>
              <ViewProductOrder />
            </PermissionRoute>
          ),
        },
        {
          path: "/support",
          element: (
            <PermissionRoute requiredPermissionId={123}>
              <Support />
            </PermissionRoute>
          ),
        },
        {
          path: "/contact-us",
          element: (
            <PermissionRoute requiredPermissionId={123}>
              <ContactUs />
            </PermissionRoute>
          ),
        },
        {
          path: "/banners",
          element: (
            <PermissionRoute requiredPermissionId={110}>
              <Banners />
            </PermissionRoute>
          ),
        },

        {
          path: "/settings",
          element: (
            <PermissionRoute requiredPermissionId={19}>
              <Settings />
            </PermissionRoute>
          ),
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
};

function App() {
  return <RouterConfig />;
}

export default App;
